import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField
} from '@mui/material';
import FormDialog from '../../common/FormDialog';
import { assessmentAPI } from '../../../services/api';

const INITIAL_WEIGHT_VALIDATION = {
  isValid: true,
  totalWeight: 0,
  includedAssessments: 0,
  activeAssessments: 0,
  message: '',
  policy: null
};

const AssessmentConfigurationFormDialog = ({
  open,
  onClose,
  onSubmit,
  editingConfig,
  saving,
  formData,
  setFormData,
  sortedClassStreamSubjectPapers,
  assessmentTypes,
  terms,
  getClassStreamPaperLabel,
  existingConfigKeys = new Set(),
  isMobile = false
}) => {
  const [weightValidation, setWeightValidation] = useState(INITIAL_WEIGHT_VALIDATION);
  const usedWeight = Number(weightValidation.totalWeight || 0);
  const contributionModel = weightValidation.policy?.contribution_model || 'weighted_sum_100';
  const recommendedWeight = contributionModel === 'single_100'
    ? (weightValidation.includedAssessments > 0 ? 0 : 100)
    : Math.max(0, 100 - usedWeight);
  const classStreamSubjectPaperId = formData.class_stream_subject_paper_id;
  const academicYear = formData.academic_year;
  const termId = formData.term_id;
  const assessmentTypeId = formData.assessment_type_id;
  const weightPercentage = formData.weight_percentage;

  useEffect(() => {
    if (editingConfig) return;
    if (!classStreamSubjectPaperId || !academicYear || !termId) {
      setWeightValidation(INITIAL_WEIGHT_VALIDATION);
      return;
    }

    assessmentAPI.validateWeights({
      class_stream_subject_paper_id: classStreamSubjectPaperId,
      academic_year: academicYear,
      term_id: termId
    })
      .then((res) => {
        const data = res?.data || {};
        setWeightValidation({
          isValid: Boolean(data.is_valid ?? false),
          totalWeight: Number(data.total_weight ?? 0),
          includedAssessments: Number(data.included_assessments ?? 0),
          activeAssessments: Number(data.active_assessments ?? 0),
          message: data.message || '',
          policy: data.policy || null
        });
      })
      .catch(() => setWeightValidation(INITIAL_WEIGHT_VALIDATION));
  }, [classStreamSubjectPaperId, academicYear, termId, editingConfig]);

  useEffect(() => {
    if (editingConfig || !assessmentTypeId) return;
    if (String(weightPercentage ?? '') !== '') return;
    setFormData((prev) => ({ ...prev, weight_percentage: recommendedWeight }));
  }, [
    editingConfig,
    assessmentTypeId,
    weightPercentage,
    recommendedWeight,
    setFormData
  ]);

  const handleTypeChange = (value) => {
    const weight = value ? recommendedWeight : '';
    setFormData({ ...formData, assessment_type_id: value?.id || '', weight_percentage: weight });
  };

  const configKey = `${formData.class_stream_subject_paper_id}-${formData.assessment_type_id}-${formData.term_id}-${formData.academic_year}`;
  const isDuplicate = !editingConfig
    && formData.class_stream_subject_paper_id
    && formData.assessment_type_id
    && formData.term_id
    && formData.academic_year
    && existingConfigKeys.has(configKey);

  const isTypeDisabled = (type) => {
    if (!formData.class_stream_subject_paper_id || !formData.term_id || !formData.academic_year) return false;
    return existingConfigKeys.has(`${formData.class_stream_subject_paper_id}-${type.id}-${formData.term_id}-${formData.academic_year}`);
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={onSubmit}
      title={editingConfig ? 'Edit Configuration' : 'Add Configuration'}
      saving={saving}
      disabled={isDuplicate}
      isMobile={isMobile}
    >
      <Stack spacing={2} sx={{ pt: 1 }}>

        {isDuplicate && (
          <Alert severity="info">This combination is already configured. Choose a different assessment type or subject paper.</Alert>
        )}
        {!editingConfig && formData.class_stream_subject_paper_id && formData.term_id && formData.academic_year && (
          <Alert severity={weightValidation.isValid ? 'success' : 'warning'}>
            {weightValidation.message || 'Weight validation is unavailable for this scope.'}
            {weightValidation.policy ? ` Policy: ${weightValidation.policy.policy_code} (${weightValidation.policy.contribution_model}).` : ''}
          </Alert>
        )}

        <Autocomplete
          fullWidth
          options={sortedClassStreamSubjectPapers}
          value={sortedClassStreamSubjectPapers.find((a) => Number(a.id) === Number(formData.class_stream_subject_paper_id)) || null}
          onChange={(_, value) => setFormData({ ...formData, class_stream_subject_paper_id: value?.id || '' })}
          getOptionLabel={(option) => getClassStreamPaperLabel(option)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText="No curriculum configured. Go to Academic Management -> Curriculum first."
          renderInput={(params) => <TextField {...params} label="Class / Stream / Subject Paper" required />}
        />

        <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
          <Autocomplete
            fullWidth
            options={assessmentTypes}
            value={assessmentTypes.find((t) => Number(t.id) === Number(formData.assessment_type_id)) || null}
            onChange={(_, value) => handleTypeChange(value)}
            getOptionLabel={(option) => option.assessment_name || option.short_name || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionDisabled={isTypeDisabled}
            renderInput={(params) => <TextField {...params} label="Assessment Type" required />}
          />
          <Autocomplete
            fullWidth
            options={terms}
            value={terms.find((t) => Number(t.id) === Number(formData.term_id)) || null}
            onChange={(_, value) => setFormData({ ...formData, term_id: value?.id || '', due_date: value?.end_date ? String(value.end_date).slice(0, 10) : formData.due_date })}
            getOptionLabel={(option) => `${option.name || 'Term'} ${option.year || ''}`.trim()}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label="Term" required />}
          />
        </Stack>

        <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
          <TextField
            fullWidth type="number" label="Academic Year"
            value={formData.academic_year}
            onChange={(e) => setFormData({ ...formData, academic_year: e.target.value })}
            inputProps={{ min: 2020, max: new Date().getFullYear() + 1 }}
            required
          />
          <TextField
            fullWidth type="number" label="Max Marks"
            value={formData.max_marks}
            onChange={(e) => setFormData({ ...formData, max_marks: e.target.value })}
            inputProps={{ min: 0, max: 1000, step: 0.01 }}
            required
          />
          <TextField
            fullWidth type="number" label="Weight %"
            value={formData.weight_percentage}
            onChange={(e) => setFormData({ ...formData, weight_percentage: e.target.value })}
            inputProps={{ min: 0, max: 100, step: 0.01 }}
            helperText={
              !editingConfig && formData.class_stream_subject_paper_id && formData.term_id && formData.academic_year
                ? `${usedWeight}% currently included in final (${weightValidation.includedAssessments} included assessment(s))`
                : ''
            }
            required
          />
        </Stack>

        <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Assessment Component</InputLabel>
            <Select
              label="Assessment Component"
              value={formData.assessment_component || 'examination'}
              onChange={(e) => setFormData({ ...formData, assessment_component: e.target.value })}
            >
              <MenuItem value="examination">Examination (Default)</MenuItem>
              <MenuItem value="continuous_assessment">Continuous Assessment</MenuItem>
              <MenuItem value="rubric">Rubric</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth type="number" label="Sequence Order"
            value={formData.sequence_order ?? ''}
            onChange={(e) => setFormData({ ...formData, sequence_order: e.target.value })}
            inputProps={{ min: 1, max: 200 }}
            helperText="Higher order takes precedence for single-assessment policies"
          />
        </Stack>

        <Stack direction={isMobile ? 'column' : 'row'} spacing={2} alignItems="center">
          <TextField
            fullWidth type="date" label="Due Date"
            value={formData.due_date}
            onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
          <Box display="flex" gap={2} sx={{ minWidth: 'fit-content' }}>
            <FormControlLabel
              control={<Switch checked={formData.is_compulsory} onChange={(e) => setFormData({ ...formData, is_compulsory: e.target.checked })} />}
              label="Compulsory"
            />
            <FormControlLabel
              control={<Switch checked={Boolean(formData.include_in_final ?? true)} onChange={(e) => setFormData({ ...formData, include_in_final: e.target.checked })} />}
              label="Include in Final"
            />
            <FormControlLabel
              control={<Switch checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />}
              label="Active"
            />
          </Box>
        </Stack>

      </Stack>
    </FormDialog>
  );
};

export default AssessmentConfigurationFormDialog;
